'use client'

import { useEffect, useRef } from 'react'
import { Analytics } from '@vercel/analytics/next'
import { useCapacitorBridge } from '@/components/capacitor-bridge-provider'
import { scheduleWhenIdle } from '@/lib/schedule-idle'

/**
 * Renders Vercel Analytics and injects GTM only on web (not in native apps).
 * This ensures no tracking scripts run in iOS/Android to comply with
 * App Store Guideline 5.1.2(i) (ATT).
 *
 * GTM se inyecta cuando el hilo principal está ocioso, no en `load`: el
 * contenedor (gtm.js + los pixels que carga, p.ej. TikTok) costaba ~106 ms de
 * frame bloqueante justo cuando el visitante da su primer toque, y eso se
 * heredaba como input delay en el INP. Ver lib/schedule-idle.ts.
 */
export function WebAnalytics() {
  const { isNativeApp } = useCapacitorBridge()
  const gtmInjectedRef = useRef(false)

  // Inject GTM script only on web
  useEffect(() => {
    if (isNativeApp || gtmInjectedRef.current) return

    const injectGtm = () => {
      try {
        const w = window as any
        w.dataLayer = w.dataLayer || []
        w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        const f = document.getElementsByTagName('script')[0]
        const j = document.createElement('script')
        j.async = true
        j.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-5TSLZL7C'
        f.parentNode?.insertBefore(j, f)
        gtmInjectedRef.current = true
      } catch (e) {
        console.warn('GTM init error:', e)
      }
    }

    return scheduleWhenIdle(injectGtm)
  }, [isNativeApp])

  if (isNativeApp) {
    return null
  }

  return <Analytics />
}
